import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Shield, ClipboardCheck, Thermometer, Plus, FileText, CheckCircle2
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import ChecklistenTab from "../components/haccp/ChecklistenTab";
import AufgabenTab from "../components/haccp/AufgabenTab";
import TemperaturTab from "../components/haccp/TemperaturTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASK_CATEGORIES = [
  { value: "haccp", label: "HACCP Kontrolle", icon: Shield, color: "text-red-500" },
  { value: "temperature", label: "Temperatur", icon: Thermometer, color: "text-blue-500" },
  { value: "cleaning", label: "Reinigung", icon: Shield, color: "text-cyan-500" },
  { value: "pest", label: "Schaedlingskontrolle", icon: Shield, color: "text-orange-500" },
  { value: "storage", label: "Lagerung", icon: Shield, color: "text-purple-500" },
  { value: "opening", label: "Oeffnung", icon: Shield, color: "text-green-500" },
  { value: "closing", label: "Schliessung", icon: Shield, color: "text-yellow-500" },
];

export const HaccpHygiene = () => {
  const [activeTab, setActiveTab] = useState("checklists");
  const [checklists, setChecklists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [temperatureLogs, setTemperatureLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      const [checklistsRes, tasksRes, templatesRes] = await Promise.all([
        axios.get(`${API}/checklists`, { withCredentials: true }),
        axios.get(`${API}/tasks`, { withCredentials: true }),
        axios.get(`${API}/checklists/templates`, { withCredentials: true })
      ]);
      setChecklists(checklistsRes.data.checklists || []);
      setTasks(tasksRes.data.tasks || []);
      setTemplates(templatesRes.data.templates || []);
      const savedLogs = localStorage.getItem('margevio-temp-logs');
      if (savedLogs) setTemperatureLogs(JSON.parse(savedLogs));
    } catch (err) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const getCategoryInfo = (cat) => TASK_CATEGORIES.find(c => c.value === cat) || TASK_CATEGORIES[0];

  const handleAddChecklist = async (data) => {
    try {
      await axios.post(`${API}/checklists`, data, { withCredentials: true });
      toast.success("Checkliste erstellt");
      loadData();
    } catch (err) { toast.error("Fehler beim Erstellen"); }
  };

  const handleDeleteChecklist = async (id) => {
    if (!window.confirm("Checkliste wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/checklists/${id}`, { withCredentials: true });
      toast.success("Checkliste geloescht");
      loadData();
    } catch (err) { toast.error("Fehler beim Loeschen"); }
  };

  const handleAddTask = async (data) => {
    try {
      await axios.post(`${API}/tasks`, data, { withCredentials: true });
      toast.success("Aufgabe erstellt");
      loadData();
    } catch (err) { toast.error("Fehler beim Erstellen"); }
  };

  const handleDeleteTask = async (id) => {
    if (!window.confirm("Aufgabe wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/tasks/${id}`, { withCredentials: true });
      toast.success("Aufgabe geloescht");
      loadData();
    } catch (err) { toast.error("Fehler beim Loeschen"); }
  };

  const handleCompleteTask = async (id) => {
    try {
      await axios.post(`${API}/tasks/${id}/complete`, {}, { withCredentials: true });
      toast.success("Aufgabe abgeschlossen");
      loadData();
    } catch (err) { toast.error("Fehler"); }
  };

  const handleAddTempLog = (newTemp) => {
    const log = {
      id: Date.now(), ...newTemp,
      temperature: parseFloat(newTemp.temperature),
      timestamp: new Date().toISOString(),
      isOk: parseFloat(newTemp.temperature) >= -18 && parseFloat(newTemp.temperature) <= 7
    };
    const updatedLogs = [log, ...temperatureLogs];
    setTemperatureLogs(updatedLogs);
    localStorage.setItem('margevio-temp-logs', JSON.stringify(updatedLogs));
    toast.success("Temperatur protokolliert");
  };

  const handleAddFromTemplate = async (template) => {
    try {
      await axios.post(`${API}/checklists/from-template`, { template_id: template.id }, { withCredentials: true });
      toast.success(`"${template.name}" hinzugefuegt`);
      setShowTemplates(false);
      loadData();
    } catch (err) { toast.error("Fehler beim Hinzufuegen"); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div></div>;
  }

  return (
    <div className="space-y-6 p-6" data-testid="haccp-hygiene-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-white flex items-center gap-2">
            <Shield className="text-red-500" size={28} /> HACCP & Hygiene
          </h1>
          <p className="text-slate-400 text-sm mt-1">Gesundheitsamt-konforme Checklisten, Temperaturkontrolle & Aufgaben</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowTemplates(true)} className="gap-2 border-slate-600" data-testid="templates-btn">
            <FileText size={16} /> Vorlagen
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="checklists" className="gap-2 data-[state=active]:bg-slate-700">
            <ClipboardCheck size={16} /><span className="hidden sm:inline">Checklisten</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="gap-2 data-[state=active]:bg-slate-700">
            <CheckCircle2 size={16} /><span className="hidden sm:inline">Aufgaben</span>
          </TabsTrigger>
          <TabsTrigger value="temperature" className="gap-2 data-[state=active]:bg-slate-700">
            <Thermometer size={16} /><span className="hidden sm:inline">Temperatur</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checklists" className="mt-4">
          <ChecklistenTab checklists={checklists} getCategoryInfo={getCategoryInfo}
            onDelete={handleDeleteChecklist} onAdd={handleAddChecklist} />
        </TabsContent>

        <TabsContent value="tasks" className="mt-4">
          <AufgabenTab tasks={tasks} getCategoryInfo={getCategoryInfo}
            onDelete={handleDeleteTask} onComplete={handleCompleteTask} onAdd={handleAddTask} />
        </TabsContent>

        <TabsContent value="temperature" className="mt-4">
          <TemperaturTab temperatureLogs={temperatureLogs} onAdd={handleAddTempLog} />
        </TabsContent>
      </Tabs>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Vorlagen vom Gesundheitsamt</DialogTitle>
            <DialogDescription className="text-slate-400">Standard-Checklisten nach HACCP-Richtlinien</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {templates.map((template) => (
              <div key={template.id} className="border border-slate-700 rounded-xl p-4 hover:bg-slate-700/50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-white">{template.name}</h3>
                    {template.description && <p className="text-sm text-slate-400 mt-1">{template.description}</p>}
                    <p className="text-xs text-slate-500 mt-2">{template.tasks?.length || 0} Checkpunkte</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleAddFromTemplate(template)} className="gap-1 border-slate-600">
                    <Plus size={14} /> Hinzufuegen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HaccpHygiene;
